import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule, Router, ActivatedRoute } from "@angular/router";
import { SidebarService } from "../../core/services/sidebar.service";
import { SidebarMenu } from "../../core/models/sidebar-menu.model";
import { SidebarMenuChild } from "../../core/models/sidebar-menu-child.model";
import { SharedService } from "../../core/services/shared.service";
import { UserRole } from "../../shared/enums/userRoles.enum";
import { lastValueFrom, pluck } from "rxjs";
import { UserRoleName } from "../../shared/enums/userRolesName.enum";
import { UserManagementService } from "../../core/services/user-management.service";
import { SidebarMenuData } from "../../shared/constants/sidebarMenu.constant";
import { ValidateLogin } from "../../core/models/validate-login.dto";
import { GenericResponseDto } from "../../core/models/generic-response.dto";
import { ProfileFormService } from "../../core/services/profile-form.service";
import { UserSupportMap } from "../../core/models/profile/userSupport";
import { UserSupportMapService } from "../../core/services/user-support-map.service";
import { environment } from "../../../environments/environment";

@Component({
  selector: "jni-sidebar",
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"],
})
export class SidebarComponent implements OnInit {
  submenuStates = new Map<string, boolean>();
  sideBarMenuData = SidebarMenuData;
  userRole = "";
  userLoggedEmail = "";
  sidebarMenus: SidebarMenu[] = [];
  buildNumber = environment.buildNumber;

  @Output() collapseChange = new EventEmitter<boolean>(); // EventEmitter to notify parent
  isBCUser = true;
  bcLedgerFlag = false;
  whoISupportChecked = false;
  isInventoryEmailsInWhoISupportTable = false;

  constructor(
    private sidebarService: SidebarService,
    private router: Router,
    private _sharedService: SharedService,
    private route: ActivatedRoute,
    private _userManagement: UserManagementService,
    private _profileFormService: ProfileFormService,
    private _userSupportMapService: UserSupportMapService
  ) {}

  ngOnInit(): void {
    this.getUserProfile();
  }
  toggleSidebar() {
    this.sidebarService.toggleSidebar();
  }

  toggleSubmenu(menuLabel: SidebarMenu) {
    this.setMenuStates(menuLabel.title);
    menuLabel.isCollapsed = !menuLabel.isCollapsed;
    if (menuLabel.url.includes("https://")) {
      window.open(menuLabel.url, "_blank");
      return;
    }
    if (menuLabel.url && menuLabel.url !== "#") {
      if (menuLabel.title?.toLowerCase()?.includes("orders")) {
        this._sharedService.clearDashbaordPocAdditionalFiltersObject();
      }
      const [path, queryString] = menuLabel.url.split("?");
      const queryParams = queryString
        ? Object.fromEntries(new URLSearchParams(queryString))
        : {};
      this.sidebarService.setMenu(path);
      this.router.navigate([path], { queryParams });
    }
  }

  toggleSubmenuChild(child: SidebarMenuChild) {
    this.toggleClass(child);
    this.setMenuStates(child.title);

    if (child.url) {
      const [path, queryString] = child.url.split("?");
      const queryParams = queryString
        ? Object.fromEntries(new URLSearchParams(queryString))
        : {};
      this.sidebarService.setMenu(path);
      this.router.navigate([path], { queryParams });
    }
  }

  setMenuStates(menuLabel: string) {
    const currentState = this.submenuStates.get(menuLabel) ?? false;
    this.submenuStates.set(menuLabel, !currentState);
  }

  isSubmenuVisible(menuLabel: string): boolean {
    return this.submenuStates.get(menuLabel) ?? false;
  }

  isMenuActive(menu: SidebarMenu): boolean | undefined {
    return (
      menu.child && menu.child.length > 0 && this.isSubmenuVisible(menu.title)
    );
  }
  toggleClass(obj: SidebarMenuChild) {
    obj.isActivated = !obj.isActivated;
  }

  navigate(url: string) {
    if (url.includes("https://")) {
      window.open(url, "_blank");
      return;
    }

    const [path, queryString] = url.split("?");
    const queryParams = queryString
      ? Object.fromEntries(new URLSearchParams(queryString))
      : {};
    this.router.navigate([path], { queryParams });
  }

  navigateToDashbaord() {
    this.router.navigate(["dashboard"]);
  }

  isValidRole(menu: SidebarMenu | SidebarMenuChild) {
    if (menu?.roles) {
      if (
        menu?.roles.includes(UserRole[this.userRole as keyof typeof UserRole])
      ) {
        return true;
      } else {
        return false;
      }
    } else if (!menu.roles) {
      return true;
    }
    if (menu?.emails) {
      if (menu?.emails.includes(this.userLoggedEmail)) {
        return true;
      } else {
        return false;
      }
    }
    return true;
  }

  setSideBar(): void {
    //getting value from resolver
    this.route.data.pipe(pluck("userRole")).subscribe((role: string) => {
      this.userRole = role.split(" ").join("");
      let userObject;
      let userEmail = "";
      const userJson = localStorage.getItem("user");
      const impersonatorEmail = localStorage.getItem("impersonator");
      if (userJson) {
        userObject = JSON.parse(userJson);
        userEmail =
          impersonatorEmail && impersonatorEmail !== ""
            ? impersonatorEmail
            : userObject.idTokenClaims.preferred_username;
        this.userLoggedEmail = userEmail;
      }

      this.sidebarMenus = this.sideBarMenuData.map(x => {
        //setting the isVisible value to true if user roles matches the defined roles.
        if (
          x.roles?.includes(UserRole[this.userRole as keyof typeof UserRole]) ||
          x.emails.includes(userEmail)
        ) {
          x.isVisible = true;
        } else {
          x.isVisible = false;
        }

        if (
          x.title === "BC Ledger" &&
          this.userRole.toLowerCase() !== UserRoleName.Admin.toLowerCase() &&
          this.userRole.toLowerCase() !== UserRoleName.Manager.toLowerCase() &&
          !this.isBCUser &&
          !this.bcLedgerFlag
        ) {
          x.isVisible = false;
        }

        if (x.title == "Inventory") {
          x.isVisible = this.whoISupportChecked === true;
        }
        return x;
      });
    });
  }

  getUserProfile() {
    this._userManagement
      .validateLogin()
      .pipe()
      .subscribe((res: GenericResponseDto<ValidateLogin>) => {
        this.isBCUser = res.data.isBCUser;
        this.setSideBar();
        this.getUserById(res?.data?.userId);
      });
  }

  getUserById(userId: number) {
    this._profileFormService.getUsersById(userId).subscribe({
      next: async res => {
        if (res?.data) {
          this.getWhoISupportTable(res?.data?.loginEmail);
        }
      },
    });
  }

  whoISupportTableData: UserSupportMap[] = [];
  async getWhoISupportTable(userEmail: string) {
    const res = await lastValueFrom(
      this._userSupportMapService.getWhoISupportTableRecord(userEmail).pipe()
    );
    this.whoISupportTableData = res.data;

    // check if any who I support ledger flag is true
    const indexFlag = this.whoISupportTableData.findIndex(
      x => x.isLedger === true
    );
    if (indexFlag !== -1) {
      this.bcLedgerFlag = true;
    }

    this.isInventoryEmailsInWhoISupportTable = this.whoISupportTableData.some(
      x =>
        x.user.loginEmail === "booz.allen@nadel.com" ||
        x.user.loginEmail === "red.hat@nadel.com"
    );

    if (
      !this.isInventoryEmailsInWhoISupportTable &&
      this.userRole.toLowerCase() !== UserRoleName.Admin.toLowerCase()
    ) {
      this.sideBarMenuData = this.sideBarMenuData.filter(
        x => x.title !== "Inventory"
      );
    }

    this.whoISupportChecked = true;

    this.setSideBar();
  }
}
